// Navigation bar component for InfluenceHub MVP
// Provides main navigation and user menu
// Shows different options based on authentication status and user role

import React from 'react';
import { useAuth } from '../../context/AuthContext.jsx';
import { useCurrentInfluencerProfile } from '../../hooks/useCurrentInfluencerProfile';

const Navbar = ({ onNavigate }) => {
  const { user, profile, signOut } = useAuth();
  const { influencerProfile } = useCurrentInfluencerProfile();

  const handleSignOut = async () => {
    try {
      await signOut();
      onNavigate('login');
    } catch (error) {
      console.error('Error signing out:', error);
    }
  };

  return (
    <nav className="bg-white shadow-sm border-b border-gray-200">
      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
        <div className="flex justify-between items-center h-16">
          {/* Logo and brand name */}
          <div className="flex items-center gap-2">
            <img
              src="/LogoSquare.png"
              alt=""
              className="h-6 w-6 sm:h-8 sm:w-8 rounded flex-shrink-0"
              onError={(e) => { e.target.style.display = 'none'; }}
            />
            <h1 className="text-xl font-bold text-gray-900">
              InfluenceHub
            </h1>
          </div>

          {/* User menu with profile and logout options */}
          <div className="flex items-center space-x-4">
            {user && profile && (
              <>
                {/* User info */}
                <div className="flex items-center space-x-3">
                  <div className="text-right">
                    <p className="text-sm font-medium text-gray-900">
                      {profile.userType === 'influencer' && influencerProfile
                        ? (influencerProfile.displayName || influencerProfile.identity?.displayName || profile.displayName || 'User')
                        : (profile.displayName || profile.companyName || profile.name || 'User')}
                    </p>
                    <p className="text-xs text-gray-500 capitalize">
                      {profile.userType}
                    </p>
                  </div>
                  
                  {/* User avatar */}
                  {(() => {
                    // For influencers, use influencerProfile (single source of truth)
                    // For brands, use profile from AuthContext
                    const isInfluencer = profile.userType === 'influencer';
                    const effectiveProfile = isInfluencer ? influencerProfile : profile;
                    
                    const avatarUrl = isInfluencer
                      ? (effectiveProfile?.photoURL || effectiveProfile?.profileImageUrl || effectiveProfile?.avatarUrl || null)
                      : (profile?.logoUrl || profile?.photoUrl || profile?.profile?.media?.profilePictureUrl || profile?.profilePictureUrl || profile?.avatarUrl || profile?.photoURL || null);
                    
                    return (
                      <div className="w-8 h-8 rounded-full bg-slate-100 border border-slate-200 overflow-hidden flex items-center justify-center relative">
                        {avatarUrl ? (
                          <img 
                            src={avatarUrl} 
                            alt={effectiveProfile?.displayName || profile.displayName || 'User'}
                            className="w-full h-full object-cover"
                            onError={(e) => {
                              // Fallback to icon if image fails to load
                              if (e.target) {
                                e.target.style.display = 'none';
                                const fallback = e.target.parentElement?.querySelector('.avatar-fallback');
                                if (fallback) fallback.style.display = 'flex';
                              }
                            }}
                          />
                        ) : null}
                        {/* Fallback icon */}
                        <div className={`avatar-fallback absolute inset-0 flex items-center justify-center ${avatarUrl ? 'hidden' : ''}`}>
                          <svg className="h-4 w-4 text-slate-400" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                            <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M16 7a4 4 0 11-8 0 4 4 0 018 0zM12 14a7 7 0 00-7 7h14a7 7 0 00-7-7z" />
                          </svg>
                        </div>
                      </div>
                    );
                  })()}
                </div>

                {/* Logout button */}
                <button
                  onClick={handleSignOut}
                  className="text-sm text-gray-500 hover:text-gray-700 px-3 py-2 rounded-md hover:bg-gray-100 transition-colors"
                >
                  Logout
                </button>
              </>
            )}
          </div>
        </div>
      </div>
    </nav>
  );
};

export default Navbar;
